import { Component, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ChartConfiguration } from 'chart.js';
import moment from 'moment';
import { BaseChartDirective } from 'ng2-charts';
import { NgxSpinnerService } from 'ngx-spinner';
import { firstValueFrom } from 'rxjs';
import { ReportApiService } from '../apis/report-api.service';
import { LocalStorageService } from '../lib/local-storage.service';
import { IRefreshableComponent } from '../model/refreshable-component.interface';
import { ReportRequest, ReportResponseData, TypeOfVisitList } from '../model/report-request';
import { environment } from 'src/environments/environment';
import { Common } from '../lib/common-methods';

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss']
})
export class ReportsComponent implements OnInit, IRefreshableComponent {

  private lang: string;
  private isFetching: boolean = false;

  constructor(private translate: TranslateService,
    private localStorageService: LocalStorageService,
    private service: ReportApiService,
    private spinner: NgxSpinnerService,) {
    this.lang = this.translate.currentLang;
  }

  @ViewChild(BaseChartDirective)
  public chart: BaseChartDirective | null = null;

  hasMadeRequest: boolean = false;

  filters = new ReportsFilter();
  reportData: ReportResponseData = {};

  barChartData: any = {};

  barChartOptions: any = {};

  times: { display: string, value: string }[] = [];
  coversFrom: { display: string, value: string }[] = [];
  covers: { display: string, value: string }[] = [];

  customerTypes: { display: string, value: string }[] = [];

  visitTypes: { display: string, value: string, depth: number, instance: TypeOfVisitList }[] = [];
  purposes: { display: string, value: string }[] = [];
  extraInfos: { display: string, value: string }[] = [];

  ngOnInit(): void {
    this.updateParameters();

    this.prepareChart();
  }

  async refresh(): Promise<void> {
    await this.updateParameters();

    if (this.hasMadeRequest == false) {
      return;
    }

    this.fetch();
  }

  private loadingSpinnerTimeoutId: null | ReturnType<typeof setTimeout> = null;

  private showLoader() {
    this.loadingSpinnerTimeoutId = setTimeout(() => {
      this.spinner.show("report_spinner");
    }, environment.requestOverlayThreashold);
  }

  private hideLoader() {
    if (this.loadingSpinnerTimeoutId != null) {
      clearTimeout(this.loadingSpinnerTimeoutId);
    }

    this.spinner.hide("report_spinner");
  }

  fetch(): void {
    if (this.isFetching === true) {
      return;
    }

    this.isFetching = true;
    this.showLoader();

    var hourFrom = this.filters.timeFrom + ":00";
    var hourTo = this.filters.timeTo + ":59";

    let from = moment(moment(this.filters.dateFrom).format("yyyy-MM-DD") + " " + hourFrom);
    let to = moment(moment(this.filters.dateTo).format("yyyy-MM-DD") + " " + hourTo);
    if (parseInt(this.filters.timeFrom.split(":")[0]) < Common.WORKING_DAY_START_HOUR) {
      from.add(1, 'day');
    }
    if (parseInt(this.filters.timeTo.split(":")[0]) < Common.WORKING_DAY_START_HOUR) {
      to.add(1, 'day');
    }

    var dateFrom = from.format("yyyy-MM-DD") + " " + hourFrom;
    var dateTo = to.format("yyyy-MM-DD") + " " + hourTo;

    const payload: ReportRequest = {
      category: this.filters.extraInfo,
      coversRange: { coversFrom: this.filters.coversFrom, coversTo: this.filters.coversTo },
      period: { dateFrom: dateFrom, dateTo: dateTo },
      purpose: this.filters.purpose,
      restaurantId: this.localStorageService.getRestaurantId() ?? "",
      shift: { timeFrom: hourFrom, timeTo: hourTo },
      typeOfReservation: this.filters.customerType,
      typeOfVisit: this.filters.visitType
    };

    this.service.getData(payload).subscribe(a => {
      this.isFetching = false;
      this.hideLoader();

      this.hasMadeRequest = true;
      this.reportData = a.periodOverviewReportUpdated;

      this.barChartData.datasets[1].data = [
        this.reportData.completedCovers,
        this.reportData.cancelledCovers,
        this.reportData.noShowCovers,
        this.reportData.upcomingCovers,
        this.reportData.rejectedCovers,
      ];

      this.barChartData.datasets[0].data = [
        this.reportData.completedReservations,
        this.reportData.cancelledReservations,
        this.reportData.noShowReservations,
        this.reportData.upcomingReservations,
        this.reportData.rejectedReservations,
      ];

      this.chart?.chart?.update();
    });
  }

  private async updateParameters() {
    this.fillCustomerTypes();
    this.fillTimeSlots();
    this.fillCovers();
    this.filters.timeFrom = this.times[0].value;
    this.filters.timeTo = this.times[this.times.length - 1].value;

    const params = await firstValueFrom(this.service.getParams(this.localStorageService.getRestaurantId() + ""));

    this.extraInfos = params.categoryList
      .sort((a, b) => a.order - b.order)
      .map((s: any) => ({ display: this.getLocalizedDescription(s, false), value: s.value }));

    this.purposes = params.purposeList
      .sort((a, b) => a.order - b.order)
      .map((s: any) => ({ display: this.getPurposeLocalizedDescription(s, true), value: s.value }));

    this.parseTypeOfVisits(params.typeOfVisitList);

    this.filters.extraInfo = this.extraInfos.length > 0 ? this.extraInfos[0].value : "";
    this.filters.purpose = this.purposes.length > 0 ? this.purposes[0].value : "";
    this.filters.visitType = this.visitTypes.length > 0 ? this.visitTypes[0].instance : null;
  }

  private parseTypeOfVisits(data: TypeOfVisitList[], depth = 0) {
    if (data == null || data.length == 0) {
      return;
    }
    data = data.sort((a, b) => a.order - b.order);
    for (let dato of data) {
      this.visitTypes.push({ display: this.getLocalizedDescription(dato, false), value: dato.typeOfVisit, depth: depth, instance: dato });
      this.parseTypeOfVisits(dato.subObjectList, depth + 1);
    }
  }

  private async prepareChart() {
    const chartColors = [
      'rgba(255, 99, 132, 0.2)',
      'rgba(255, 159, 64, 0.2)',
      'rgba(255, 205, 86, 0.2)',
      'rgba(75, 192, 192, 0.2)',
      'rgba(54, 162, 235, 0.2)',
      'rgba(153, 102, 255, 0.2)',
      'rgba(201, 203, 207, 0.2)'
    ];

    const bordersColors = [
      'rgb(255, 99, 132)',
      'rgb(255, 159, 64)',
      'rgb(255, 205, 86)',
      'rgb(75, 192, 192)',
      'rgb(54, 162, 235)',
      'rgb(153, 102, 255)',
      'rgb(201, 203, 207)'
    ];

    const justToLoadTranslations = await firstValueFrom(this.translate.get('report.status.completed'));

    this.barChartData = {
      labels: [
        this.translate.instant('report.status.completed'),
        this.translate.instant('report.status.cancelled'),
        this.translate.instant('report.status.notshown'),
        this.translate.instant('report.status.upcoming'),
        this.translate.instant('report.status.rejected')],
      datasets: [{
        label: this.translate.instant('report.chart.reservations'),
        data: [0],
        backgroundColor: ['rgba(75, 192, 192, 0.2)'],
        hoverBackgroundColor: ['rgba(75, 192, 192, 0.8)'],
        borderColor: [bordersColors[3]],
        hoverBorderColor: [bordersColors[3]],
        borderWidth: 1
      },
      {
        label: this.translate.instant('report.chart.covers'),
        data: [0],
        backgroundColor: ['rgba(54, 162, 235, 0.2)'],
        hoverBackgroundColor: ['rgba(54, 162, 235, 0.8)'],
        borderColor: [bordersColors[4]],
        borderWidth: 1
      }]
    };

    this.barChartOptions = {
      scales: {
        y: {
          beginAtZero: true
        }
      }
    };
  }

  private fillCustomerTypes() {
    const types = [...this.localStorageService.getCustomerTypes()];

    this.customerTypes = [{ display: this.translate.instant('report.allM'), value: "All" }, ...types
      .sort((a, b) => parseInt(a.order) - parseInt(b.order))
      .map(t => ({ display: this.getLocalizedDescription(t), value: t.value }))];
  }

  private fillTimeSlots() {
    const slots: { display: string, value: string }[] = [];

    const start = Common.WORKING_DAY_START_HOUR;
    const step = 15;

    const current = moment()
      .set("hours", start)
      .set("minutes", 0);

    while (true) {
      const time = current.format("HH:mm");
      slots.push({ display: time, value: time });
      current.add(step, "minutes");
      if (time == "05:45") {
        break;
      }
    }

    this.times = slots;
  }

  private fillCovers() {
    this.covers.push({ display: this.translate.instant('report.max'), value: 'MAX' });

    for (let i = 0; i < 200; i++) {
      this.covers.push({ display: i + "", value: i + "" });
      this.coversFrom.push({ display: i + "", value: i + "" });
    }
  }

  private capitalizeFirstLetter(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  private getLocalizedDescription(obj: any, isMale: boolean | null = null) {
    const translation = obj["description_" + this.lang] ?? obj["description" + this.capitalizeFirstLetter(this.lang)]
      ?? obj["description_en"] ?? obj["descriptionEn"];
    if (isMale != null && translation == "All") {
      return isMale ? this.translate.instant('report.allM') : this.translate.instant('report.allF');
    }
    return translation;
  }

  private getPurposeLocalizedDescription(obj: any, isMale: boolean | null = null) {
    
    if (obj["description_" + this.lang] != null) {
      return this.getLocalizedDescription(obj, isMale);
    }

    if (obj.value.toLowerCase() === 'all') {
      return this.translate.instant('blocking.labels.all');
    } else if (obj.value.toLowerCase() === 'food') {
      return this.translate.instant('blocking.labels.food');
    } else if (obj.value.toLowerCase() === 'drink') {
      return this.translate.instant('blocking.labels.drink');
    }

    return this.getLocalizedDescription(obj, isMale);
  }
}

class ReportsFilter {

  constructor() {
    var tommorow = new Date();
    tommorow.setDate(tommorow.getDate() + 1);
    this.dateTo = tommorow;
  }

  dateFrom: Date = new Date();
  dateTo: Date;
  timeFrom: string = "01:00";
  timeTo: string = "01:00";
  coversFrom: string = "0";
  coversTo: string = "MAX";
  customerType: string = "All";
  visitType: TypeOfVisitList | null = null;
  purpose: string = "";
  extraInfo: string = "";
}